// frontend/src/services/api.js

/**
 * API Service for Yuliart Application
 * Handles all API calls with domain-agnostic configuration
 */

// Determine API base URL based on environment
export const getApiBaseUrl = () => {
  // In production (Docker), API is proxied through nginx at /api
  // In development, use environment variable or localhost

  if (process.env.NODE_ENV === "production") {
    // Use relative URL - nginx will proxy to backend
    return "/api";
  }

  // Development: use environment variable or default to localhost
  return process.env.REACT_APP_API_URL || "http://localhost:5000/api";
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Generic fetch wrapper with error handling
 * @param {string} endpoint - API endpoint (e.g., '/categories')
 * @param {object} options - Fetch options
 * @returns {Promise<any>}
 */
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(
        error.message || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
};

const buildQueryString = (path, params) => {
   const query = new URLSearchParams(params);
   return `${path}?${query.toString()}`;
}

/**
 * API Service
 */
const api = {
  /**
   * Get all categories
   * @returns {Promise<Array>} Array of category objects
   */
  getCategories: async () => {
    return apiFetch("/categories");
  },

  /**
   * Get single category by ID
   * @param {string} categoryId - Category ID
   * @returns {Promise<Object>} Category object
   */
  getCategoryById: async (categoryId) => {
    return apiFetch(`/categories/${categoryId}`);
  },

  /**
   * Get artworks with pagination and filtering
   * @param {string} categoryId - Category ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 12)
   * @param {string} search - Optional search query
   * @param {string} tags - Optional comma-separated tags
   * @returns {Promise<Object>} Object with artworks array and pagination info
   */
  getArtworks: async (
    categoryId,
    page = 1,
    limit = 12,
    search = "",
    tags = "",
  ) => {
    const params = new URLSearchParams({
      categoryId,
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (tags) params.append("tags", tags);

    return apiFetch(`/artworks?${params.toString()}`);
  },

  getAllStudentArtworks: async (
    page = 1,
    limit = 12,
    search = "",
    tags = "",
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (tags) params.append("tags", tags);

    return apiFetch(`/artworks/bystudents?${params.toString()}`);
  },

  /**
   * Get single artwork by ID
   * @param {string} artworkId - Artwork ID
   * @returns {Promise<Object>} Artwork object
   */
  getArtwork: async (artworkId) => {
    return apiFetch(`/artworks/${artworkId}`);
  },

  /**
   * Search artworks
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Search results
   */
  searchArtworks: async (query, page = 1, limit = 12) => {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      limit: limit.toString(),
    });

    return apiFetch(`/artworks/search?${params.toString()}`);
  },

  getAllArtists: async (params = {}) => {
    return apiFetch("/artists/all", { params });
  },

  getArtists: async (params = {}) => {
    return apiFetch("/artists", { params });
  },

  getArtistById: async (artistId) => {
    return apiFetch(`/artists/${artistId}`);
  },

  getArtworksByArtist: async (artistId, page = 1, limit = 12, viewable = true) => {
    const path = buildQueryString(`/artworks/byartist/${artistId}`, {
      page,
      limit,
      viewable,
    });
    console.log("getArtworksByArtist", path);
    return apiFetch(path);
  },

  getArtTagsByCategory: async (categoryId, params = {}) => {
    const path = buildQueryString("/arttags", { categoryId, ...params });
    console.log("getArtTagsByCategory", path);
    return apiFetch(path);
  },

  getArtTagById: async (artTagId) => {
    return apiFetch(`/arttags/${artTagId}`);
  },

  getArtworksByTag: async (
    categoryId,
    tagName,
    page = 1,
    limit = 12,
    viewable = true,
  ) => {
    const path = buildQueryString(`/artworks/${categoryId}/${tagName}`, {
      page,
      limit,
      viewable,
    });
    console.log("getArtworksByTag", path);
    return apiFetch(path);
  },

  /**
   * Create new category (admin)
   * @param {Object} categoryData - Category data
   * @returns {Promise<Object>} Created category
   */
  createCategory: async (categoryData) => {
    return apiFetch("/categories", {
      method: "POST",
      body: JSON.stringify(categoryData),
    });
  },

  /**
   * Update category (admin)
   * @param {string} categoryId - Category ID
   * @param {Object} categoryData - Updated category data
   * @returns {Promise<Object>} Updated category
   */
  updateCategory: async (categoryId, categoryData) => {
    return apiFetch(`/categories/${categoryId}`, {
      method: "PUT",
      body: JSON.stringify(categoryData),
    });
  },

  /**
   * Delete category (admin)
   * @param {string} categoryId - Category ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteCategory: async (categoryId) => {
    return apiFetch(`/categories/${categoryId}`, {
      method: "DELETE",
    });
  },

  /**
   * Create new artwork (admin)
   * @param {Object} artworkData - Artwork data
   * @returns {Promise<Object>} Created artwork
   */
  createArtwork: async (artworkData) => {
    return apiFetch("/artworks", {
      method: "POST",
      body: JSON.stringify(artworkData),
    });
  },

  /**
   * Update artwork (admin)
   * @param {string} artworkId - Artwork ID
   * @param {Object} artworkData - Updated artwork data
   * @returns {Promise<Object>} Updated artwork
   */
  updateArtwork: async (artworkId, artworkData) => {
    return apiFetch(`/artworks/${artworkId}`, {
      method: "PUT",
      body: JSON.stringify(artworkData),
    });
  },

  /**
   * Delete artwork (admin)
   * @param {string} artworkId - Artwork ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteArtwork: async (artworkId) => {
    return apiFetch(`/artworks/${artworkId}`, {
      method: "DELETE",
    });
  },

  /**
   * Get all mediums
   * @param {boolean} activeOnly - Filter only active mediums
   * @returns {Promise<Array>} Array of medium objects
   */
  getMediums: async (activeOnly = true) => {
    const params = activeOnly ? "?active=true" : "";
    return apiFetch(`/mediums${params}`);
  },

  /**
   * Get single medium by ID
   * @param {string} mediumId - Medium ID
   * @returns {Promise<Object>} Medium object
   */
  getMediumById: async (mediumId) => {
    return apiFetch(`/mediums/${mediumId}`);
  },

  /**
   * Create new medium (admin)
   * @param {Object} mediumData - Medium data
   * @returns {Promise<Object>} Created medium
   */
  createMedium: async (mediumData) => {
    return apiFetch("/mediums", {
      method: "POST",
      body: JSON.stringify(mediumData),
    });
  },

  /**
   * Update medium (admin)
   * @param {string} mediumId - Medium ID
   * @param {Object} mediumData - Updated medium data
   * @returns {Promise<Object>} Updated medium
   */
  updateMedium: async (mediumId, mediumData) => {
    return apiFetch(`/mediums/${mediumId}`, {
      method: "PUT",
      body: JSON.stringify(mediumData),
    });
  },

  /**
   * Delete medium (admin)
   * @param {string} mediumId - Medium ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteMedium: async (mediumId) => {
    return apiFetch(`/mediums/${mediumId}`, {
      method: "DELETE",
    });
  },

  /**
   * Health check
   * @returns {Promise<Object>} Health status
   */
  healthCheck: async () => {
    return apiFetch("/health");
  },
};

export default api;

// Export the base URL for debugging purposes
export const getBaseUrl = () => API_BASE_URL;
